import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';

import Card from '@/components/Card';
import TextComponent from '@/components/TextComponent';
import CustomDatePicker from '@/components/CustomDatePicker';
import SelectComponent from '@/components/SelectComponent';
import Tooltip from '@/components/Tooltip';
import { submitEnterKey } from '@/utils/form';
import Label from '@/components/SearchFilter/Label';
import { filterLimitList, GSelectOption, SelectOption } from '@/assets/data/selectOptions';
import { ReactComponent as CalendarIcon } from '@/assets/images/common/calendar.svg';
import { ReactComponent as Question } from '@/assets/images/layout/question.svg';
import {
  EOrderSearchType,
  EPreparingSortType,
  EPreparingStatusType,
  OrdersParams,
  PreparingOrdersParams
} from '@/apis/order/interface';
import useOrderQuery from '@/hooks/order/useOrderQuery';
import GlobalAlert from '@/components/GlobalAlert/GlobalAlert';
import theme from '@/styles/theme';
import LineButton, { ButtonColorType } from '@/components/ButtonComponent/LineButton';

type Props = {
  body: PreparingOrdersParams;
  setBody: (body: PreparingOrdersParams) => void;
  isAdditionalOrderPharmacy: boolean;
};

const ReqeusetedOrderSearchOptions: Array<GSelectOption<EOrderSearchType>> = [
  { label: '환자명', value: EOrderSearchType.PATIENT_NAME },
  { label: '연락처', value: EOrderSearchType.PATIENT_PHONE }
];

const RequestedOrderSortOptions: Array<GSelectOption<EPreparingSortType>> = [
  { label: '오래된순', value: EPreparingSortType.PAID_AT_ASC },
  { label: '최신순', value: EPreparingSortType.PAID_AT_DESC }
];

const RequestedOrderFilterOptions: Array<
  GSelectOption<EPreparingStatusType | null>
> = [
  { label: '전체', value: null },
  { label: '조제진행', value: EPreparingStatusType.REQUEST_PICKUP },
  { label: '수거대기', value: EPreparingStatusType.PICKUP_CHECK }
];

const SearchHeader = (props: Props) => {
  const { body, setBody, isAdditionalOrderPharmacy } = props;
  const { orderCJExcelDownloadMutation } = useOrderQuery();
  const bodyRef = useRef<{ body: PreparingOrdersParams }>({ body });
  const [startDate, setStartDate] = useState<string>(body.startDate);
  const [endDate, setEndDate] = useState<string>(body.endDate);

  const [searchType, setSearchType] = useState<GSelectOption<EOrderSearchType>>(
    ReqeusetedOrderSearchOptions[0]
  );

  const [searchInput, setSearchInput] = useState<string>('');

  const [statusFilter, setStatusFilter] = useState<
    GSelectOption<EPreparingStatusType | null>
  >(RequestedOrderFilterOptions[0]);
  const [sort, setSort] = useState<GSelectOption<EPreparingSortType>>(
    RequestedOrderSortOptions[0]
  );

  const [limit, setLimit] = useState<SelectOption>(filterLimitList[0]);

  const onChangeSearchType = (type: GSelectOption<EOrderSearchType>) => {
    setSearchType(type);
  };

  const onSubmit = () => {
    const searchOption: Partial<OrdersParams> = {
      patientName: undefined,
      patientPhone: undefined
    };

    if (searchType.value === EOrderSearchType.PATIENT_NAME) {
      searchOption.patientName = searchInput;
    }
    if (searchType.value === EOrderSearchType.PATIENT_PHONE) {
      searchOption.patientPhone = searchInput;
    }
    setBody({
      ...body,
      ...searchOption
    });
  };

  const onChangeEndDate = (date: Date | null) => {
    setEndDate(moment(date).format('YYYY-MM-DD'));
    if (moment(date).diff(moment(startDate).toDate(), 'days') < 0) {
      setStartDate(moment(date).subtract(30, 'd').format('YYYY-MM-DD'));

      setBody({
        ...body,
        startDate: moment(date).subtract(30, 'd').format('YYYY-MM-DD'),
        endDate: moment(date).format('YYYY-MM-DD'),
        page: 1
      });
      return;
    }
    setBody({ ...body, endDate: moment(date).format('YYYY-MM-DD') });
  };

  const onChangeStartDate = (date: Date | null) => {
    setStartDate(moment(date).format('YYYY-MM-DD'));
    setBody({ ...body, startDate: moment(date).format('YYYY-MM-DD'), page: 1 });
  };

  const handleChangeFilter = (status: GSelectOption<EPreparingStatusType>) => {
    setStatusFilter(status);
    setBody({ ...body, status: status.value, page: 1 });
  };

  const handleChangeSort = (sort: GSelectOption<EPreparingSortType>) => {
    setSort(sort);
    setBody({ ...body, sort: sort.value, page: 1 });
  };

  const handleChangeLimit = (limit: SelectOption) => {
    setLimit(limit);
    setBody({ ...body, limit: limit.value as number, page: 1 });
  };

  const clear = () => {
    setBody({ ...bodyRef?.current?.body });
    setStartDate(bodyRef?.current?.body.startDate);
    setEndDate(bodyRef?.current?.body.endDate);
    setSearchInput('');
    setSearchType(ReqeusetedOrderSearchOptions[0]);
    setStatusFilter(RequestedOrderFilterOptions[0]);
    setSort(RequestedOrderSortOptions[0]);
    setLimit(filterLimitList[0]);
  };

  const onDownloadCJExcel = () => {
    if (!isAdditionalOrderPharmacy) {
      orderCJExcelDownloadMutation.mutate({
        from: startDate,
        to: endDate
      });
      return;
    }

    GlobalAlert.show({
      title: '추가 주문 상품을 챙기셨나요?',
      body: (
        <span style={{ color: theme.DF_COLOR.GRAY400 }}>
          약품이 수거되기 전에 꼭 챙겨주세요.
        </span>
      ),
      buttons: [
        { text: '닫기', onClick: () => GlobalAlert.hide() },
        {
          text: <span style={{ color: theme.DF_COLOR.PRIMARY700 }}>챙겼어요</span>,
          onClick: () =>
            orderCJExcelDownloadMutation.mutate({
              from: startDate,
              to: endDate
            })
        }
      ]
    });
  };

  return (
    <Wrapper>
      <Card>
        <CardBody>
          <Label style={{ marginRight: 16, boxSizing: 'border-box' }}>
            검색 기간
          </Label>
          <CustomDatePicker
            selected={moment(startDate).toDate()}
            onChange={onChangeStartDate}
            selectsStart
            startDate={moment(startDate).toDate()}
            endDate={moment(endDate).toDate()}
            minDate={moment(endDate).subtract(30, 'd').toDate()}
            maxDate={moment().toDate()}
            dateFormat={'yyyy.MM.dd'}
            customInput={<DateInput />}
          />
          <Dash>-</Dash>
          <CustomDatePicker
            selected={moment(endDate).toDate()}
            onChange={onChangeEndDate}
            selectsEnd
            startDate={moment(startDate).toDate()}
            endDate={moment(endDate).toDate()}
            dateFormat={'yyyy.MM.dd'}
            maxDate={moment().toDate()}
            customInput={<DateInput />}
          />

          <Label style={{ marginLeft: 48, marginRight: 16 }}>검색 조건</Label>

          <SelectComponent
            isSearchable={false}
            options={ReqeusetedOrderSearchOptions}
            defaultValue={ReqeusetedOrderSearchOptions[0]}
            onChange={e => onChangeSearchType(e)}
            selectStyle={{
              width: '120px',
              height: '40px'
            }}
            singleValue={{
              color: '#000'
            }}
          />

          <TextInput
            style={{ flex: 1, height: '100%' }}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => submitEnterKey(e, onSubmit)}
          />

          <LineButton
            type={ButtonColorType.GREEN}
            onClick={onSubmit}
            style={{
              padding: '0 40px',
              height: '100%',
              borderRadius: 4,
              fontSize: 17
            }}>
            조회
          </LineButton>
        </CardBody>
      </Card>
      <Row>
        <ExcelButtons>
          <LineButton
            type={ButtonColorType.GREEN}
            onClick={onDownloadCJExcel}
            style={{
              padding: '8px 43px',
              height: '100%'
            }}>
            CJ택배 업로드용 엑셀 다운로드
          </LineButton>
        </ExcelButtons>
        <SelectBox>
          <Question
            style={{ outline: 'none' }}
            data-tip
            data-for="order"
            width={18}
            height={18}
          />
          <Tooltip id="order" place="top" type="info" effect="solid">
            <TextComponent color={'#fff'}>
              정렬기준은 처방전 결제 시간을 기준으로 보여집니다.
            </TextComponent>
          </Tooltip>
          <SelectComponent
            useDefault
            selectStyle={{ marginRight: 8, marginLeft: 11 }}
            onChange={handleChangeFilter}
            options={RequestedOrderFilterOptions as Array<SelectOption>}
            defaultValue={RequestedOrderFilterOptions[0] as SelectOption}
            value={statusFilter}
          />
          <SelectComponent
            useDefault
            selectStyle={{ marginRight: 8 }}
            onChange={handleChangeSort}
            options={RequestedOrderSortOptions}
            defaultValue={RequestedOrderSortOptions[0]}
            value={sort}
          />
          <SelectComponent
            useDefault
            onChange={handleChangeLimit}
            options={filterLimitList}
            defaultValue={filterLimitList[0]}
            value={limit}
          />
        </SelectBox>
      </Row>
    </Wrapper>
  );
};

export default SearchHeader;

const Wrapper = styled.div``;
const CardBody = styled.div`
  padding: 22px 30px;
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  height: 84px;
`;

const DateInput = styled.input<{ width?: number }>`
  border: 1px solid ${theme.DF_COLOR.GRAY100};
  padding: 11px 15px;
  border-radius: 5px;
  max-width: ${({ width }) => (width ? width : 140)}px;
  font-size: 16px;
`;

const Dash = styled(TextComponent)`
  margin: 0 16px;
`;

const Calendar = styled(CalendarIcon)`
  margin-left: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 30px;
  margin-bottom: 8px;
`;

const ExcelButtons = styled.div``;
const SelectBox = styled.div`
  display: flex;
  align-items: center;
`;
const TextInput = styled.input`
  margin-left: 8px;
  margin-right: 28px;
  border: 1px solid ${theme.DF_COLOR.GRAY100};
  width: 100%;
  border-radius: 5px;
  font-size: 16px;
  padding: 0 15px;

  &:disabled {
    opacity: 0.5;
  }
`;
